import { UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useInvoiceContext } from 'src/presentations/components/organisms/actionDetails/InvoiceContextProvider';
import { InvoiceDisputeDto, InvoiceDisputeUpdateRequestDto, InvoiceIssueSummaryDto, InvoiceSummaryDto } from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';
import { CacheKeys, cacheKeysUtil } from 'src/utils/cacheKeysUtil';
import { DeviationWorkflowAction } from 'src/infra/types/deviation';

export type DisputeOptionType = 'ACCEPT_INVOICE' | 'REJECT_INVOICE' | 'CANCEL_DISPUTE';

export interface UpdateArgs {
  disputeId: string;
  data: InvoiceDisputeUpdateRequestDto;
}

type NextWorkflowActionOption = InvoiceSummaryDto['nextWorkflowAction'];

interface SubmitArgs {
  disputeId: string;
  deviationWorkflowAction: NextWorkflowActionOption;
  reason?: string;
}

export const useDisputeDetails = (disputeId: string): UseQueryResult<InvoiceDisputeDto> =>
  useQuery(
    cacheKeysUtil.dispute(disputeId),
    async (): Promise<InvoiceDisputeDto> => {
      const response = await tFinancialService.api.getInvoiceDisputeDetail(disputeId);
      const { data } = response;
      return data.data;
    },
    { enabled: !!disputeId }
  );

export const useGetInvoiceIssueSummary = (invoiceId: string): UseQueryResult<InvoiceIssueSummaryDto> =>
  useQuery(
    cacheKeysUtil.invoiceIssueSummary(invoiceId),
    async (): Promise<InvoiceIssueSummaryDto> => {
      const response = await tFinancialService.api.getInvoiceIssueSummary(invoiceId);
      const { data } = response;
      return data.data;
    },
    { enabled: !!invoiceId }
  );

export const useUpdateDispute = () => {
  const queryClient = useQueryClient();
  const { invoiceId } = useInvoiceContext();
  return useMutation(async (args: UpdateArgs) => tFinancialService.api.updateInvoiceDispute(args.disputeId, args.data), {
    onSuccess: (data) =>
      Promise.all([
        queryClient.invalidateQueries(cacheKeysUtil.getVersion(invoiceId)),
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.actionDetails],
        }),
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.invoiceSummary],
        }),
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.getShipment],
        }),
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.invoiceStats],
        }),
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.userResponse],
        }),
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.approvalUserResponse],
        }),
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.questionnaire],
        }),
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.approvalQuestionnaire],
        }),
        queryClient.invalidateQueries(cacheKeysUtil.getDisputeCreditNote(data.data.data.id)),
      ]),
  });
};

export const useSubmitDisputeWorkflow = () => {
  const queryClient = useQueryClient();
  const { invoiceId } = useInvoiceContext();
  return useMutation(
    async (args: SubmitArgs) => {
      const data = args?.reason && args?.deviationWorkflowAction !== DeviationWorkflowAction.CANCEL_DISPUTE ? { reason: args.reason } : {};
      return tFinancialService.api.performDeviationWorkflowAction1(args.disputeId, args.deviationWorkflowAction, data);
    },
    {
      onSuccess: async () =>
        Promise.all([
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.actionDetails],
          }),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.invoiceSummary],
          }),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.getShipment],
          }),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.invoiceStats],
          }),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.userResponse],
          }),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.approvalUserResponse],
          }),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.questionnaire],
          }),
          queryClient.invalidateQueries({
            exact: false,
            queryKey: [CacheKeys.approvalQuestionnaire],
          }),
          queryClient.invalidateQueries(cacheKeysUtil.getVersion(invoiceId)),
        ]),
    }
  );
};

export const useDisputeOptions = (billedEntityId?: number): UseQueryResult<DisputeOptionType[]> =>
  useQuery(cacheKeysUtil.getDisputeOptions(billedEntityId), async (): Promise<DisputeOptionType[]> => {
    const response = await tFinancialService.api.getDisputeOptions({ billedEntityId });
    const { data } = response;
    return data.data || [];
  });
